import { useSyncExternalStore, type ReactNode } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { Archive, BookLock, Columns3, LayoutDashboard, Pin, PinOff, X } from 'lucide-react';
import { clsx } from 'clsx';
import { useBestellung } from '@/hooks/useBestellung';
import { useToast } from '@/lib/toast';
import {
  ABLAGE_HOECHSTENS,
  ablageAbonnieren,
  ablageArt,
  ablageEntfernen,
  ablagePfad,
  ablageStand,
  ablegen
} from '@/lib/ablage';

// Die Ablage (02.11.2026) — Knopf oben, Pillen unten. Was und warum:
// lib/ablage.ts.

function useAblage(): string[] {
  return useSyncExternalStore(ablageAbonnieren, ablageStand, () => []);
}

function useAktuellerPfad(): string {
  const pathname = useLocation({ select: (l) => l.pathname });
  const searchStr = useLocation({ select: (l) => l.searchStr });
  return ablagePfad(pathname, searchStr);
}

/** „Ablegen" oben rechts — nur auf Seiten, die sich ablegen lassen. */
export function AblegenKnopf({ kompakt = false }: { kompakt?: boolean }) {
  const pfad = useAktuellerPfad();
  const ablage = useAblage();
  const toast = useToast();
  if (ablageArt(pfad) === null) return null;
  const liegt = ablage.includes(pfad);

  function umschalten(): void {
    if (liegt) {
      ablageEntfernen(pfad);
      return;
    }
    if (!ablegen(pfad)) {
      toast('error', `Die Ablage ist voll (${ABLAGE_HOECHSTENS}). Erst eine Pille unten mit × entfernen.`);
    }
  }

  const Symbol = liegt ? PinOff : Pin;
  const text = liegt ? 'Aus der Ablage nehmen' : 'Ablegen';
  return (
    <button
      type="button"
      onClick={umschalten}
      aria-pressed={liegt}
      title={liegt ? 'Liegt unten in der Ablage — antippen nimmt sie heraus' : 'Diese Seite unten ablegen, um später zurückzukommen'}
      className={clsx(
        'inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-1.5 rounded-md border px-3 text-sm',
        liegt ? 'border-akzent bg-akzent-sanft text-akzent-stark' : 'border-line text-ink-soft hover:border-akzent hover:text-akzent'
      )}
    >
      <Symbol aria-hidden className="h-4 w-4" />
      {kompakt ? <span className="sr-only">{text}</span> : liegt ? 'Abgelegt' : 'Ablegen'}
    </button>
  );
}

function Rest({ children }: { children: ReactNode }) {
  return <span className="min-w-0 truncate text-ink-soft">{children}</span>;
}

function Beschriftung({ pfad }: { pfad: string }) {
  const a = ablageArt(pfad);
  // Hook immer rufen, auch wenn es keine Bestellung ist (leer = aus).
  const best = useBestellung(a?.art === 'bestellung' ? (a.nr ?? '') : '');
  if (a === null) return null;
  switch (a.art) {
    case 'bestellung': {
      const k = best.data?.bestellung.kunde;
      const name = k ? (k.nachname || k.firma || k.anzeigename) : null;
      return (
        <>
          <span className="shrink-0 font-serif text-akzent">{a.nr}</span>
          {name && <Rest>{name}</Rest>}
        </>
      );
    }
    case 'dashboard':
      return (
        <>
          <LayoutDashboard aria-hidden className="h-3.5 w-3.5 shrink-0 text-gold-dark" />
          <span className="shrink-0 text-gold-dark">Dashboard</span>
          {(a.q || a.quelle) && (
            <Rest>{[a.q ? `„${a.q}“` : null, a.quelle === 'glomer' ? 'Glomer' : a.quelle === 'laden' ? 'Laden' : null].filter(Boolean).join(', ')}</Rest>
          )}
        </>
      );
    case 'archiv':
      return (
        <>
          <Archive aria-hidden className="h-3.5 w-3.5 shrink-0 text-gold-dark" />
          <span className="shrink-0 text-gold-dark">Archiv</span>
          {a.q && <Rest>„{a.q}“</Rest>}
        </>
      );
    case 'reserviert':
      return (
        <>
          <BookLock aria-hidden className="h-3.5 w-3.5 shrink-0 text-gold-dark" />
          <span className="shrink-0 text-gold-dark">Reserviert?</span>
          {a.q && <Rest>„{a.q}“</Rest>}
        </>
      );
    case 'kanban':
      return (
        <>
          <Columns3 aria-hidden className="h-3.5 w-3.5 shrink-0 text-gold-dark" />
          <span className="shrink-0 text-gold-dark">Kanban</span>
        </>
      );
  }
}

/** Die Pillen am unteren Rand. Ohne Ablage: nichts, nicht einmal ein Rand. */
export function AblageLeiste() {
  const ablage = useAblage();
  const aktuell = useAktuellerPfad();
  const navigate = useNavigate();
  if (ablage.length === 0) return null;
  return (
    <nav
      aria-label="Ablage"
      data-testid="ablage-leiste"
      className="flex shrink-0 items-center gap-2 overflow-x-auto border-t border-line bg-cream-warm px-4 py-1.5"
    >
      <span className="shrink-0 text-[11px] uppercase tracking-wide text-gold-dark">Abgelegt</span>
      {ablage.map((pfad) => {
        const hier = pfad === aktuell;
        return (
          <span
            key={pfad}
            className={clsx(
              'inline-flex min-h-[40px] max-w-[16rem] shrink-0 items-center rounded-full border text-sm',
              hier ? 'border-akzent bg-akzent-sanft' : 'border-line bg-cream'
            )}
          >
            <a
              href={pfad}
              aria-current={hier ? 'page' : undefined}
              onClick={(e) => {
                // Strg-/Mittelklick dem Browser lassen (neues Fenster).
                if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
                e.preventDefault();
                void navigate({ href: pfad });
              }}
              className="inline-flex min-h-[40px] min-w-0 items-center gap-2 pl-3.5 pr-1"
            >
              <Beschriftung pfad={pfad} />
            </a>
            <button
              type="button"
              onClick={() => ablageEntfernen(pfad)}
              aria-label="Aus der Ablage entfernen"
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-ink-faint hover:text-coral"
            >
              <X aria-hidden className="h-4 w-4" />
            </button>
          </span>
        );
      })}
    </nav>
  );
}
